import { useEffect, useRef, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { ArrowUpIcon, MegaphoneIcon, SquareIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Frame, FrameFooter, FramePanel } from "@/components/ui/frame";
import { Textarea } from "@/components/ui/textarea";
import { useDashboardStore } from "@/stores";
import {
  chipRangesIn,
  mentionQueryAt,
  renderComposerHighlights,
  withMentionTokens,
  type MentionableCampaign,
} from "./campaignMentions";

interface ComposerProps {
  /** A turn is running: sending is held and the button becomes Stop. */
  busy: boolean;
  /** Sending is blocked for another reason (e.g. a question must be answered first). */
  blocked?: boolean;
  placeholder?: string;
  hint?: string;
  onSend: (text: string) => void;
  /** Cancel the running turn. */
  onStop?: () => void;
  autoFocus?: boolean;
}

/**
 * Auto-growing message input (Frame-based, like the rest of the dashboard)
 * with a send/stop button and `@` campaign tagging: typing `@` opens a searchable list of the org's campaigns; a pick
 * completes `@Name` — painted as a chip by a layer behind the transparent
 * textarea — and seals it so the next Enter sends. On send each mention is
 * rewritten to `@[Name](campaign:<id>)`, so Amy always gets the campaign's
 * name AND _id inside the message (see campaignMentions.tsx).
 *
 * The field stays editable while Amy works; only sending waits.
 */
export function Composer({
  busy,
  blocked = false,
  placeholder = "Ask Amy about your campaigns and creators…",
  hint = "Enter to send, Shift + Enter for a new line · @ to tag a campaign",
  onSend,
  onStop,
  autoFocus = true,
}: ComposerProps) {
  const [input, setInput] = useState("");
  const [caret, setCaret] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  // Esc-dismissal, keyed by the mention's `@` index — a NEW `@` re-arms it.
  const [dismissedAt, setDismissedAt] = useState<number | null>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  // Campaigns picked from the list this message (name → _id); resolved to
  // wire tokens at send. A hand-edited mention just degrades to plain text.
  const chosenRef = useRef(new Map<string, string>());
  const keyboardNavRef = useRef(false);

  const campaigns = useDashboardStore((s) => s.campaigns) as MentionableCampaign[] | undefined;

  // chosenRef only mutates inside handlers that also setInput, so reading it
  // during render always sees the current picks.
  const chipRanges = chipRangesIn(input, chosenRef.current);
  const mention = mentionQueryAt(input, caret, chipRanges);
  const mentionQuery = mention?.query;
  const mentionStart = mention?.start;
  let matches: MentionableCampaign[] = [];
  if (mention) {
    const q = mention.query.trim().toLowerCase();
    matches = (campaigns ?? [])
      .filter((c) => (c.campaign_name ?? "").toLowerCase().includes(q))
      .slice(0, 8);
  }
  const open = mention !== null && mention.start !== dismissedAt && matches.length > 0;
  const idx = Math.max(0, Math.min(activeIdx, matches.length - 1));
  const canSend = input.trim().length > 0 && !busy && !blocked;

  useEffect(() => {
    setActiveIdx(0);
  }, [mentionQuery, mentionStart]);

  // Keep the highlighted row visible on keyboard navigation only — hover
  // already has the row under the cursor, and scrolling then would jitter.
  useEffect(() => {
    if (!keyboardNavRef.current) return;
    keyboardNavRef.current = false;
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  const syncBackdrop = (ta: HTMLTextAreaElement) => {
    if (backdropRef.current) backdropRef.current.scrollTop = ta.scrollTop;
  };

  // Auto-grow up to the max height (field-sizing isn't everywhere yet).
  const resize = (ta: HTMLTextAreaElement) => {
    ta.style.height = "auto";
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
    syncBackdrop(ta);
  };

  const selectCampaign = (c: MentionableCampaign) => {
    if (!mention) return;
    const before = input.slice(0, mention.start);
    const after = input.slice(caret);
    const inserted = `@${c.campaign_name} `;
    chosenRef.current.set(c.campaign_name, c._id);
    setInput(before + inserted + after);
    const pos = before.length + inserted.length;
    setCaret(pos);
    setDismissedAt(null);
    requestAnimationFrame(() => {
      const ta = taRef.current;
      if (!ta) return;
      ta.focus();
      ta.setSelectionRange(pos, pos);
      resize(ta);
    });
  };

  const submit = () => {
    if (!canSend) return;
    onSend(withMentionTokens(input.trim(), chosenRef.current));
    chosenRef.current.clear();
    setInput("");
    setCaret(0);
    setDismissedAt(null);
    if (taRef.current) taRef.current.style.height = "auto";
  };

  return (
    <div className="relative px-2 pb-2">
      <div className="relative mx-auto w-full max-w-3xl">
        {open && (
          <div
            className="absolute inset-x-0 bottom-full z-50 mb-2 overflow-hidden rounded-xl border bg-popover shadow-lg"
            // Keep the textarea focused (and the mention alive) through clicks.
            onMouseDown={(e) => e.preventDefault()}
          >
            <p className="px-3 pt-2 pb-1 text-xs font-medium text-muted-foreground">Tag a campaign</p>
            <div
              ref={listRef}
              role="listbox"
              className="max-h-64 overflow-y-auto p-1 [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]"
            >
              {matches.map((c, i) => (
                <button
                  key={c._id}
                  type="button"
                  role="option"
                  aria-selected={i === idx}
                  data-idx={i}
                  onClick={() => selectCampaign(c)}
                  onMouseEnter={() => setActiveIdx(i)}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm",
                    i === idx ? "bg-muted-foreground/10 text-foreground" : "text-foreground/80"
                  )}
                >
                  <MegaphoneIcon className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate">{c.campaign_name}</span>
                  {c.status === "POPULATING" && (
                    <Spinner className="ml-auto size-3.5 shrink-0 text-muted-foreground" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* An input, not a section: keeps its chrome whatever the app-wide Frame variant. */}
        <Frame variant="default" className="rounded-2xl p-0.75">
          <FramePanel className="flex items-end gap-2 rounded-[calc(var(--radius-2xl)-3px)] p-2 transition focus-within:border-primary focus-within:ring-3 focus-within:ring-primary/15">
            <div className="relative min-w-0 flex-1">
              {/* The VISIBLE text layer: same metrics as the textarea (text-sm,
                  px-2 py-2, pre-wrap), which renders its own text transparent
                  on top — so mentions can be real chips while caret,
                  selection and wrapping stay native. */}
              <div
                ref={backdropRef}
                aria-hidden
                className="pointer-events-none absolute inset-0 overflow-hidden px-2 py-2 text-sm whitespace-pre-wrap wrap-break-word"
              >
                {renderComposerHighlights(input, chipRanges)}
              </div>
              <Textarea
                ref={taRef}
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  setCaret(e.target.selectionStart ?? e.target.value.length);
                  resize(e.target);
                }}
                onScroll={(e) => syncBackdrop(e.currentTarget)}
                onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
                onKeyDown={(e) => {
                  if (open && mention) {
                    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                      e.preventDefault();
                      keyboardNavRef.current = true;
                      setActiveIdx(
                        e.key === "ArrowDown"
                          ? (idx + 1) % matches.length
                          : (idx - 1 + matches.length) % matches.length
                      );
                      return;
                    }
                    if (e.key === "Enter" || e.key === "Tab") {
                      e.preventDefault();
                      selectCampaign(matches[idx]!);
                      return;
                    }
                    if (e.key === "Escape") {
                      e.preventDefault();
                      setDismissedAt(mention.start);
                      return;
                    }
                  }
                  if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submit();
                  }
                }}
                autoFocus={autoFocus}
                placeholder={placeholder}
                aria-label="Message Amy"
                rows={1}
                className="relative max-h-64 resize-none border-0 bg-transparent px-2 py-2 text-transparent caret-foreground shadow-none focus-visible:ring-0 [scrollbar-color:--alpha(var(--foreground)/20%)_transparent]"
              />
            </div>
            {busy && onStop ? (
              <Button
                onClick={onStop}
                size="icon"
                variant="neutral"
                className="size-8 shrink-0 [&>svg]:size-3"
                aria-label="Stop generating"
              >
                <SquareIcon className="fill-current" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={!canSend}
                size="icon"
                className="size-8 shrink-0"
                aria-label="Send"
              >
                {busy ? <Spinner className="size-4" /> : <ArrowUpIcon className="size-4" />}
              </Button>
            )}
          </FramePanel>
          <FrameFooter className="pl-3">{hint}</FrameFooter>
        </Frame>
      </div>
    </div>
  );
}
